import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { LocationMap } from "@/components/LocationMap";
import { Loading, Empty, ErrorState } from "@/components/States";
import { inputCls, chip, btnPrimary, btnOutline, label } from "@/lib/ui";

const DEFAULT = { q: "", category: "", min_price: "", max_price: "", condition: [], radius: "", sort: "newest", make: "", min_year: "", max_year: "", max_mileage: "" };
const VEHICLE_KEYS = ["make", "min_year", "max_year", "max_mileage"];

const VehicleFilters = ({ f, setF }) => (
  <div data-testid="vehicle-filters" className="space-y-2">
    <span className={label}>Vehicle</span>
    <input data-testid="filter-make" placeholder="Make (e.g. Honda)" value={f.make} onChange={(e) => setF({ ...f, make: e.target.value })} className={inputCls} />
    <div className="flex gap-2">
      <input data-testid="filter-min-year" type="number" placeholder="Year from" value={f.min_year} onChange={(e) => setF({ ...f, min_year: e.target.value })} className={inputCls} />
      <input data-testid="filter-max-year" type="number" placeholder="Year to" value={f.max_year} onChange={(e) => setF({ ...f, max_year: e.target.value })} className={inputCls} />
    </div>
    <input data-testid="filter-max-mileage" type="number" placeholder="Max mileage" value={f.max_mileage} onChange={(e) => setF({ ...f, max_mileage: e.target.value })} className={inputCls} />
  </div>
);

export const useToggleSave = (setItems) => {
  const { user } = useAuth();
  const nav = useNavigate();
  return async (item) => {
    if (!user) return nav("/login", { state: { from: `/listing/${item.id}` } });
    try {
      if (item.saved) await api.delete(`/listings/${item.id}/save`);
      else await api.post(`/listings/${item.id}/save`);
      setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, saved: !item.saved } : x)));
      toast.success(item.saved ? "Removed from saved" : "Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
};

const Filters = ({ f, setF, loc, items, onPick, onLocate, locating, onApply }) => (
  <div className="space-y-6">
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className={label}>Location</span>
        <button type="button" data-testid="use-my-location-btn" disabled={locating} onClick={onLocate} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800 disabled:opacity-50">
          <LocateFixed className="h-4 w-4" /> {locating ? "Finding you…" : "Use my current location"}
        </button>
      </div>
      <p data-testid="filter-location-label" className="text-sm text-slate-700">{loc ? <>Searching near <b>{loc.area}, {loc.state}</b> · {loc.zip}</> : "Loading location…"}</p>
      {loc && <LocationMap loc={loc} radius={Number(f.radius) || 0} items={items} onPick={onPick} />}
      <p className="text-xs text-slate-500">Tap the map to search anywhere in the US. Pins show approximate ZIP areas, never exact addresses.</p>
    </div>
    <div>
      <span className={label}>Distance</span>
      <div className="flex flex-wrap gap-2">
        {["", "5", "10", "25", "50", "100"].map((r) => <button key={r} data-testid={`filter-radius-${r || "any"}`} className={chip(f.radius === r)} onClick={() => setF({ ...f, radius: r })}>{r ? `${r} mi` : "Any"}</button>)}
      </div>
    </div>
    <div>
      <span className={label}>Price</span>
      <div className="flex gap-2">
        <input data-testid="filter-min-price" type="number" placeholder="Min" value={f.min_price} onChange={(e) => setF({ ...f, min_price: e.target.value })} className={inputCls} />
        <input data-testid="filter-max-price" type="number" placeholder="Max" value={f.max_price} onChange={(e) => setF({ ...f, max_price: e.target.value })} className={inputCls} />
      </div>
    </div>
    {f.category === "vehicles" && <VehicleFilters f={f} setF={setF} />}
    <div>
      <span className={label}>Condition</span>
      <div className="flex flex-wrap gap-2">
        {Object.entries(CONDITION_LABELS).map(([k, v]) => (
          <button key={k} data-testid={`filter-condition-${k}`} className={chip(f.condition.includes(k))}
            onClick={() => setF({ ...f, condition: f.condition.includes(k) ? f.condition.filter((c) => c !== k) : [...f.condition, k] })}>{v}</button>
        ))}
      </div>
    </div>
    <div>
      <span className={label}>Sort by</span>
      <select data-testid="filter-sort-select" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} className={inputCls}>
        <option value="newest">Newest</option><option value="distance">Nearest</option>
        <option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option>
      </select>
    </div>
    <div className="flex gap-2">
      <button data-testid="filter-apply-btn" className={btnPrimary + " flex-1"} onClick={onApply}>Show results</button>
      <button data-testid="filter-reset-btn" className={btnOutline} onClick={() => setF({ ...DEFAULT, q: f.q, category: f.category })}>Reset</button>
    </div>
  </div>
);

export default function Browse() {
  const { user } = useAuth();
  const [f, setF] = useState(DEFAULT);
  const [applied, setApplied] = useState(DEFAULT);
  const [zip, setZip] = useState(localStorage.getItem("ft_zip") || user?.zip || "91401");
  const [loc, setLoc] = useState(null);
  const [locating, setLocating] = useState(false);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const toggleSave = useToggleSave(setItems);

  useEffect(() => {
    api.get(`/geo/zip/${zip}`).then(({ data }) => setLoc(data)).catch(() => setZip("91401"));
  }, [zip]);

  const pick = async (lat, lng) => {
    try {
      const { data } = await api.get("/geo/nearest", { params: { lat, lng } });
      localStorage.setItem("ft_zip", data.zip);
      setLoc(data);
      setZip(data.zip);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const locate = () => {
    if (!navigator.geolocation) return toast.error("Your browser can't share your location.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => { await pick(p.coords.latitude, p.coords.longitude); setLocating(false); },
      () => { setLocating(false); toast.error("Couldn't get your location. Allow location access, or tap the map instead."); },
      { timeout: 10000 },
    );
  };

  useEffect(() => {
    if (user?.zip && !localStorage.getItem("ft_zip")) setZip(user.zip);
  }, [user]);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqId.current;
    setErr("");
    setItems(null);
    const p = { zip, sort: applied.sort };
    ["q", "category", "min_price", "max_price", "radius", ...(applied.category === "vehicles" ? VEHICLE_KEYS : [])].forEach((k) => applied[k] && (p[k] = applied[k]));
    if (applied.condition.length) p.condition = applied.condition.join(",");
    try {
      const { data } = await api.get("/listings", { params: p });
      if (my === reqId.current) setItems(data);
    } catch (e) {
      if (my === reqId.current) setErr(errMsg(e));
    }
  }, [applied, zip]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (patch = {}) => {
    const next = { ...f, ...patch };
    setF(next);
    setApplied(next);
    localStorage.setItem("ft_zip", zip);
    setOpen(false);
  };
  const area = loc ? `${loc.area}, ${loc.state}` : "…";
  const activeCount = [applied.min_price, applied.max_price, applied.radius, ...(applied.category === "vehicles" ? VEHICLE_KEYS.map((k) => applied[k]) : [])].filter(Boolean).length + applied.condition.length;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Less back-and-forth.<br className="sm:hidden" /> <span className="text-blue-600">More sold.</span></h1>
        <p data-testid="hero-subheadline" className="mt-2 text-sm sm:text-base text-slate-600 max-w-xl">Schedule pickups, see buyer and seller reliability, and spend less time chasing replies.</p>
        <button data-testid="location-chip" onClick={() => setOpen(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-blue-700">
          <MapPin className="h-4 w-4 text-blue-600" /> Near {area} {applied.radius ? `· ${applied.radius} mi` : ""} · <span className="underline underline-offset-2">Change</span>
        </button>
      </div>
      <div className="flex gap-2 mb-4">
        <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); apply(); }}>
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search dressers, bikes, drills…" className={inputCls + " pl-10 h-12 rounded-2xl"} />
        </form>
        <button data-testid="open-filters-btn" onClick={() => setOpen(true)} className="relative h-12 px-4 rounded-2xl border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-2 text-sm font-semibold">
          <SlidersHorizontal className="h-4 w-4" /> <span className="hidden sm:inline">Filters</span>
          {activeCount > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">{activeCount}</span>}
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0 no-scrollbar">
        <button data-testid="category-all" className={chip(!applied.category)} onClick={() => apply({ category: "" })}>All</button>
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
          <button key={k} data-testid={`category-${k}`} className={chip(applied.category === k)} onClick={() => apply({ category: k })}>{v}</button>
        ))}
      </div>
      {err ? <ErrorState message={err} onRetry={load} /> : !items ? <Loading label="Finding items near you…" /> : items.length === 0 ? (
        <Empty title="Nothing matches yet" body="Try widening the distance, clearing filters, or check back soon." action={<button className={btnOutline} onClick={() => { setF(DEFAULT); setApplied(DEFAULT); }}>Clear filters</button>} />
      ) : (
        <div data-testid="listing-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {items.map((i) => <ListingCard key={i.id} item={i} onToggleSave={toggleSave} />)}
        </div>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="filters-sheet">
          <SheetHeader className="mb-6"><SheetTitle>Filters</SheetTitle></SheetHeader>
          <Filters f={f} setF={setF} loc={loc} items={items} onPick={pick} onLocate={locate} locating={locating} onApply={() => apply()} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
